import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Page, PageHeader } from "@/components/Bits";
import { toast } from "sonner";
import { DownloadSimple, TrendUp, Users, FileText, Receipt, CurrencyGbp } from "@phosphor-icons/react";

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

const toCSV = (rows, cols) => {
  const head = cols.map((c) => c.label).join(",");
  const body = rows.map((r) => cols.map((c) => {
    const v = r[c.key] ?? "";
    return typeof v === "string" && v.includes(",") ? `"${v}"` : v;
  }).join(",")).join("\n");
  return `${head}\n${body}`;
};

const download = (name, csv) => {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
};

function ReportTable({ id, icon: Icon, title, desc, cols, rows, footer }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-card mb-6" data-testid={`dr-${id}`}>
      <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-lg bg-orange/10 text-orangeink flex items-center justify-center shrink-0"><Icon size={18} weight="fill" /></span>
          <div>
            <h3 className="font-heading font-bold text-navy tracking-tight">{title}</h3>
            <p className="text-xs text-navy/70 mt-0.5">{desc}</p>
          </div>
        </div>
        <button onClick={() => download(id, toCSV(rows, cols))} disabled={!rows.length} data-testid={`dr-csv-${id}`}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-50">
          <DownloadSimple size={14} /> CSV
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-navy/65">No data for this report yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="overline text-navy/65 border-b border-slate-100">
              {cols.map((c) => <th key={c.key} className={`py-2.5 px-5 ${c.num ? "text-right" : "text-left"}`}>{c.label}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-navy/5">
                  {cols.map((c) => (
                    <td key={c.key} className={`py-2.5 px-5 ${c.num ? "text-right font-mono tabular text-navy" : "text-navy/75"}`}>
                      {c.num ? money(r[c.key]) : (c.fmt ? c.fmt(r[c.key]) : r[c.key])}
                    </td>
                  ))}
                </tr>
              ))}
              {footer && (
                <tr className="font-semibold text-navy bg-navy/[0.03]">
                  {cols.map((c, i) => <td key={c.key} className={`py-2.5 px-5 ${c.num ? "text-right font-mono tabular" : ""}`}>{footer[c.key] != null ? (c.num ? money(footer[c.key]) : footer[c.key]) : (i === 0 ? "Total" : "")}</td>)}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function DeferredReports() {
  const [d, setD] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/deferred-reports/forecast?months=24"),
      api.get("/deferred-reports/customer"),
      api.get("/deferred-reports/contract"),
      api.get("/deferred-reports/recognised"),
      api.get("/deferred-reports/journals"),
    ]).then(([f, c, ct, rec, j]) => setD({
      forecast: f.data, customer: c.data, contract: ct.data, recognised: rec.data, journals: j.data,
    })).catch(() => toast.error("Could not load reports"));
  }, []);

  const fc = d.forecast;
  const maxBar = fc ? Math.max(...fc.rows.map((r) => r.secured), 1) : 1;

  return (
    <Page>
      <PageHeader overline="Deferred Income" title="Revenue Recognition Reports"
        subtitle="Forward revenue, customer & contract breakdowns, and the journal trail. The full Register and Liability-at-date exports live on the Reports page." />

      {/* Revenue Recognition Forecast */}
      <div className="bg-white border border-orange/30 rounded-2xl shadow-card mb-6" data-testid="dr-forecast">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-orange/10 text-orangeink flex items-center justify-center"><TrendUp size={18} weight="fill" /></span>
            <div>
              <h3 className="font-heading font-bold text-navy tracking-tight">Revenue Recognition Forecast</h3>
              <p className="text-xs text-navy/70 mt-0.5">Secured income not yet recognised, by month (next 24)</p>
            </div>
          </div>
          {fc && <div className="text-right"><div className="font-heading font-black text-2xl text-navy tabular">{money(fc.total_secured)}</div><div className="overline text-navy/65">total secured</div></div>}
        </div>
        {fc && (
          <div className="px-5 py-5 flex items-end gap-1.5 h-40" data-testid="dr-forecast-chart">
            {fc.rows.map((r) => (
              <div key={r.month} className="flex-1 flex flex-col items-center justify-end h-full group" title={`${monthLabel(r.month)} · ${money(r.secured)}`}>
                <div className="w-full bg-orange/70 rounded-t-[2px] group-hover:bg-orange transition-colors" style={{ height: `${Math.max(2, (r.secured / maxBar) * 100)}%` }} />
                <span className="text-[0.55rem] text-navy/65 mt-1 rotate-0 truncate w-full text-center">{r.month.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {fc && <ReportTable id="customer" icon={Users} title="Customer Revenue Report"
        desc="Revenue recognised and remaining, by customer"
        cols={[{ key: "customer", label: "Customer" }, { key: "contracts", label: "Contracts", num: false },
               { key: "contract_value", label: "Contract value", num: true }, { key: "recognised", label: "Recognised", num: true },
               { key: "remaining", label: "Remaining", num: true }]}
        rows={d.customer?.rows || []} />}

      {fc && <ReportTable id="contract" icon={FileText} title="Contract Revenue Report"
        desc="Each deferred income contract with recognised and remaining balances"
        cols={[{ key: "contract", label: "Contract" }, { key: "customer", label: "Customer" },
               { key: "contract_value", label: "Value", num: true }, { key: "recognised", label: "Recognised", num: true },
               { key: "remaining", label: "Remaining", num: true }, { key: "status", label: "Status" }]}
        rows={d.contract?.rows || []} />}

      {fc && <ReportTable id="recognised" icon={CurrencyGbp} title="Revenue Recognised (this month)"
        desc="Revenue posted to the P&L in the current period"
        cols={[{ key: "month", label: "Month", fmt: monthLabel }, { key: "recognised", label: "Recognised", num: true }]}
        rows={d.recognised?.rows || []} footer={d.recognised ? { recognised: d.recognised.total } : null} />}

      {fc && <ReportTable id="journals" icon={Receipt} title="Journal Posting Report"
        desc="Every deferred income journal, its status and Xero reference"
        cols={[{ key: "date", label: "Date" }, { key: "contract", label: "Contract" }, { key: "customer", label: "Customer" },
               { key: "kind", label: "Type" }, { key: "amount", label: "Amount", num: true },
               { key: "status", label: "Status" }, { key: "xero_journal_id", label: "Xero ref" }]}
        rows={d.journals?.rows || []} />}
    </Page>
  );
}
